// Tiện ích dùng chung cho các biểu đồ tự vẽ (SVG/HTML, không thư viện).

import { vnDateKey } from '@shoptrack/common/date-range'

export interface DailyPoint {
  /** Ngày dạng 'YYYY-MM-DD' — cùng quy ước với API (ngày giờ VN của createdAt). */
  date: string
  value: number
}

/** Khóa ngày theo đúng cách API gom nhóm: ngày giờ VN (vnDateKey). */
export function dayKey(d: Date): string {
  return vnDateKey(d)
}

/**
 * Chuỗi `days` ngày liên tục kết thúc ở hôm nay, ngày nào API không trả về thì
 * điền 0. API chỉ trả những ngày CÓ đơn, vẽ thẳng sẽ làm trục thời gian co
 * lại và hai ngày cách nhau một tuần trông như liền kề.
 */
export function fillDaily<T extends { date: string }>(
  rows: T[] | undefined,
  days: number,
  pick: (row: T) => number,
  now: Date = new Date(),
): DailyPoint[] {
  const byDate = new Map<string, number>()
  for (const r of rows ?? []) byDate.set(r.date, (byDate.get(r.date) ?? 0) + pick(r))
  const out: DailyPoint[] = []
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(now.getTime() - i * 86_400_000)
    const key = dayKey(d)
    out.push({ date: key, value: byDate.get(key) ?? 0 })
  }
  return out
}

/** Như fillDaily nhưng theo khoảng ngày cố định `from` → `to` ("YYYY-MM-DD", hai đầu đều tính). */
export function fillRange<T extends { date: string }>(rows: T[] | undefined, from: string, to: string, pick: (row: T) => number): DailyPoint[] {
  const byDate = new Map<string, number>()
  for (const r of rows ?? []) byDate.set(r.date, (byDate.get(r.date) ?? 0) + pick(r))
  const out: DailyPoint[] = []
  for (let t = Date.parse(`${from}T00:00:00Z`), end = Date.parse(`${to}T00:00:00Z`); t <= end; t += 86_400_000) {
    const key = new Date(t).toISOString().slice(0, 10)
    out.push({ date: key, value: byDate.get(key) ?? 0 })
  }
  return out
}

/** Làm tròn bước chia trục về 1 / 2 / 5 × 10^k để nhãn trục là số tròn. */
function niceStep(raw: number): number {
  if (raw <= 0) return 1
  const exp = Math.floor(Math.log10(raw))
  const base = 10 ** exp
  const f = raw / base
  const nice = f <= 1 ? 1 : f <= 2 ? 2 : f <= 5 ? 5 : 10
  return nice * base
}

/** Thang trục y bắt đầu từ 0 với `count` khoảng chia tròn. */
export function niceScale(max: number, count = 4, integer = false): { max: number; ticks: number[] } {
  let step = niceStep(max / count)
  if (integer) step = Math.max(1, Math.round(step))
  const top = Math.max(step * count, Math.ceil(max / step) * step)
  const ticks: number[] = []
  for (let v = 0; v <= top + step / 2; v += step) ticks.push(v)
  return { max: top, ticks }
}

/** Tiền rút gọn cho nhãn trục và chú thích: 1,28 tỷ · 212,8 tr · 850k. */
export function compactMoney(n: number): string {
  const abs = Math.abs(n)
  const fmt = (v: number, d: number) => v.toFixed(d).replace(/\.0+$/, '').replace('.', ',')
  if (abs >= 1_000_000_000) return `${fmt(n / 1_000_000_000, 2)} tỷ`
  if (abs >= 1_000_000) return `${fmt(n / 1_000_000, abs >= 100_000_000 ? 0 : 1)} tr`
  if (abs >= 1_000) return `${Math.round(n / 1_000)}k`
  return String(Math.round(n))
}

const WEEKDAY = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7']

/** '2026-09-29' → '29/9' */
export function shortDate(key: string): string {
  const [, m, d] = key.split('-')
  return `${Number(d)}/${Number(m)}`
}

/** '2026-09-29' → 'T3, 29/9' */
export function longDate(key: string): string {
  const d = new Date(`${key}T00:00:00Z`)
  return `${WEEKDAY[d.getUTCDay()]}, ${shortDate(key)}`
}

export const countFormat = new Intl.NumberFormat('vi-VN', { maximumFractionDigits: 0 })
